<template>
  <div class="star-rating">
    <div
      v-for="(star, index) in stars"
      :key="star.index"
      class="star-container"
      :style="index < stars.length - 1 ? { 'margin-right': `${offset}px` } : {}"
    >
      <svg
        class="star-svg"
        :style="[
          { fill: getFill(star) },
          { width: `${size}px` },
          { height: `${size}px` }
        ]"
      >
        <polygon :points="getStarPoints" style="fill-rule:nonzero;" />
        <defs v-if="!simple">
          <linearGradient :id="`gradient${star.raw}`">
            <stop
              id="stop1"
              :offset="star.percent"
              stop-opacity="1"
              :stop-color="getFullFillColor(star)"
            ></stop>
            <stop
              id="stop2"
              :offset="star.percent"
              stop-opacity="0"
              :stop-color="getFullFillColor(star)"
            ></stop>
            <stop
              id="stop3"
              :offset="star.percent"
              stop-opacity="1"
              :stop-color="emptyColor"
            ></stop>
            <stop
              id="stop4"
              offset="100%"
              stop-opacity="1"
              :stop-color="emptyColor"
            ></stop>
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
</template>

<script>
import { STARS_RATING_COLOR } from "@/config/stars-rating-colors";

export default {
  name: "StarsRating",
  props: {
    rating: {
      type: Number,
      default: 0
    },
    fillColor: {
      type: String,
      default: STARS_RATING_COLOR.FILL
    },
    emptyColor: {
      type: String,
      default: STARS_RATING_COLOR.EMPTY
    },
    size: {
      type: Number,
      default: 22
    },
    offset: {
      type: Number,
      default: 5
    },
    totalStars: {
      type: Number,
      default: 5
    },
    simple: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      stars: [],
      emptyStar: 0,
      fullStar: 1
    };
  },
  computed: {
    getStarPoints() {
      const centerX = this.size / 2;
      const centerY = this.size / 2;
      const innerCircleArms = 5;
      const innerRadius = this.size / innerCircleArms;
      const innerOuterRadiusRatio = 2.5;
      const outerRadius = innerRadius * innerOuterRadiusRatio;

      return this.calcStarPoints(
        centerX,
        centerY,
        innerCircleArms,
        innerRadius,
        outerRadius
      );
    }
  },
  watch: {
    rating() {
      this.initStars();
      this.setStars();
    },
    totalStars() {
      this.initStars();
      this.setStars();
    }
  },
  created() {
    this.initStars();
    this.setStars();
  },
  methods: {
    calcStarPoints(
      centerX,
      centerY,
      innerCircleArms,
      innerRadius,
      outerRadius
    ) {
      const angle = Math.PI / innerCircleArms;
      const angleOffsetToCenterStar = 60;
      const totalArms = innerCircleArms * 2;
      let points = "";

      for (let i = 0; i < totalArms; i++) {
        const isEvenIndex = i % 2 === 0;
        const r = isEvenIndex ? outerRadius : innerRadius;
        const currX =
          centerX + Math.cos(i * angle + angleOffsetToCenterStar) * r;
        const currY =
          centerY + Math.sin(i * angle + angleOffsetToCenterStar) * r;
        points += currX + "," + currY + " ";
      }

      return points;
    },

    initStars() {
      this.stars = [];
      for (let i = 0; i < this.totalStars; i++) {
        this.stars.push({
          index: i,
          raw: this.emptyStar,
          percent: `${this.emptyStar}%`
        });
      }
    },

    setStars() {
      let fullStarsCounter = Math.floor(this.rating);
      for (let i = 0; i < this.stars.length; i++) {
        if (fullStarsCounter !== 0) {
          this.stars[i].raw = this.fullStar;
          this.stars[i].percent = this.calcStarFullness(this.stars[i]);
          fullStarsCounter--;
        } else {
          const surplus = Math.round((this.rating % 1) * 10) / 10;
          const roundedOneDecimalPoint = Math.round(surplus * 10) / 10;
          this.stars[i].raw = roundedOneDecimalPoint;
          return (this.stars[i].percent = this.calcStarFullness(this.stars[i]));
        }
      }
    },

    getFullFillColor(starData) {
      return starData.raw !== this.emptyStar ? this.fillColor : this.emptyColor;
    },

    calcStarFullness(starData) {
      return `${starData.raw * 100}%`;
    },

    getFill(star) {
      return this.simple
        ? this.getFullFillColor(star)
        : `url(#gradient${star.raw})`;
    }
  }
};
</script>

<style lang="scss" scoped>
.star-rating {
  padding: $spacing / 4 0;
  display: inline-flex;
  align-items: center;

  .star-container {
    display: flex;
  }
}
</style>
